import { eventChannel } from "redux-saga";
import { put, takeLatest } from "redux-saga/effects";
import { USER_LOGIN, USER_LOGOUT } from "redux-store/auth/actions";
import { auth } from "store/auth";
import { setSocketTokenHeaders, socket } from "websocket";

import { setWsConnErrAction, setWsConnStatusAction } from "./actions";

function onConnect(emitter) {
  emitter(setWsConnStatusAction(true));
}

function onConnectErr(emitter, err) {
  // Error messages are sometimes a subclass of Error
  // with description attached which has the status code.
  // However, sometimes they are pure JS errors without
  // status code. To always get the status code we may need
  // some sort of xhr request interceptor to check the last
  // status code.
  const errorMsg =
    err.description === undefined ? err.message : err.description;

  emitter(setWsConnErrAction(errorMsg));
}

function onDisconnect(emitter) {
  emitter(setWsConnStatusAction(false));
}

function wsConnectionEmitter(emitter) {
  const boundOnConnect = onConnect.bind(null, emitter);
  const boundOnConnectErr = onConnectErr.bind(null, emitter);
  const boundOnDisconnect = onDisconnect.bind(null, emitter);

  socket.on("connect", boundOnConnect);
  socket.on("connect_error", boundOnConnectErr);
  socket.on("disconnect", boundOnDisconnect);

  return () => {
    // Unsubscribe function
    socket.off("connect", boundOnConnect);
    socket.off("connect_error", boundOnConnectErr);
    socket.off("disconnect", boundOnDisconnect);
  };
}

/**
 * Subscribe to socket connect/disconnect events.
 */
function* connectionStatusListenerSaga() {
  const evtChan = eventChannel(wsConnectionEmitter);

  yield takeLatest(evtChan, put);
}

/* eslint-disable require-yield */
function* _loginActions() {
  // On user login, we get the auth token and reconnect.
  setSocketTokenHeaders(socket, auth.getToken());
  socket.connect();
}

function* _logoutActions() {
  socket.disconnect();
}
/* eslint-enable require-yield */

/**
 * Watch login action to reconnect WebSocket.
 */
function* watchLoginSaga() {
  yield takeLatest(USER_LOGIN, _loginActions);
}

/**
 * Watch logout action to disconnect WebSocket.
 */
function* watchLogoutSaga() {
  yield takeLatest(USER_LOGOUT, _logoutActions);
}

const connectionSagas = [watchLoginSaga(), watchLogoutSaga()];

export {
  _loginActions,
  _logoutActions,
  connectionSagas,
  connectionStatusListenerSaga,
  onConnect,
  onConnectErr,
  onDisconnect,
  watchLoginSaga,
  watchLogoutSaga,
  wsConnectionEmitter,
};
